import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Menu, Building2, ChevronDown, ChevronRight, KeyRound, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useI18n } from '@/contexts/I18nContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import ChatWidget from './ChatWidget';
import ExitSurveyBanner from './ExitSurveyBanner';
import { sidebarStructure } from '@/config/sidebarConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();

  // Force light mode — dark mode has been removed from the product
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    try { localStorage.setItem('theme', 'light'); } catch (err) { console.error("./components/DashboardLayout.jsx:", err); }
  }, []);

  const [user, setUser] = useState(null);
  const [enabledModules, setEnabledModules] = useState(null);
  const [disabledSubmodules, setDisabledSubmodules] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarGroups') || '{}'); } catch { return {}; }
  });
  const [tenantBranding, setTenantBranding] = useState({ company_name: '', logo_url: '', primary_color: '', favicon_url: '' });

  // Preserve sidebar scroll position across navigation clicks. Without this,
  // every Link click re-renders the layout and the <nav> snaps back to top,
  // forcing users to scroll down again to reach groups like HRMS or Admin.
  const navScrollRef = useRef(null);
  const handleNavScroll = () => {
    if (navScrollRef.current) {
      try { sessionStorage.setItem('sidebarScrollTop', String(navScrollRef.current.scrollTop)); } catch { /* sessionStorage unavailable */ }
    }
  };
  // useLayoutEffect runs synchronously BEFORE paint, so the user never sees
  // the "flash of scrolled-to-top" that plain useEffect produced on prod.
  useLayoutEffect(() => {
    const saved = parseInt(sessionStorage.getItem('sidebarScrollTop') || '0', 10);
    if (navScrollRef.current && !Number.isNaN(saved) && saved > 0) {
      navScrollRef.current.scrollTop = saved;
    }
  }, [location.pathname]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
    const token = localStorage.getItem('token');
    if (token) {
      // Always refresh user info from the server so role / team changes are picked up without re-login
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const fresh = res.data;
          setUser(fresh);
          localStorage.setItem('user', JSON.stringify(fresh));
        })
        .catch(() => {});
      axios.get(`${API}/auth/my-modules`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setEnabledModules(res.data.enabled_modules);
          setDisabledSubmodules(res.data.disabled_submodules || []);
        })
        .catch(() => setEnabledModules(null));
      // Fetch tenant branding
      axios.get(`${API}/branding/tenant/config`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setTenantBranding(res.data);
          // Apply favicon — remove ALL existing icon-related <link> tags first.
          // index.html ships with three (SVG + PNG + apple-touch-icon); a single
          // querySelector replace would leave the others pointing at Recruitizr.
          // A cache-buster query string forces the browser to refetch.
          if (res.data.favicon_url) {
            document.querySelectorAll(
              "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon'], link[rel='mask-icon']"
            ).forEach(n => n.parentNode && n.parentNode.removeChild(n));
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = res.data.favicon_url + (res.data.favicon_url.includes('?') ? '&' : '?') + 'v=' + Date.now();
            document.head.appendChild(link);
            // Also set the apple-touch-icon for iOS bookmarks
            const apple = document.createElement('link');
            apple.rel = 'apple-touch-icon';
            apple.href = link.href;
            document.head.appendChild(apple);
          }
          // Apply primary color as CSS variable
          if (res.data.primary_color && res.data.primary_color !== '#3b82f6') {
            const hex = res.data.primary_color;
            // Convert hex to HSL for shadcn CSS variables
            const r = parseInt(hex.slice(1,3), 16) / 255;
            const g = parseInt(hex.slice(3,5), 16) / 255;
            const b = parseInt(hex.slice(5,7), 16) / 255;
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h = 0, s = 0, l = (max + min) / 2;
            if (max !== min) {
              const d = max - min;
              s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
              if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
              else if (max === g) h = ((b - r) / d + 2) / 6;
              else h = ((r - g) / d + 4) / 6;
            }
            const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
            document.documentElement.style.setProperty('--primary', hsl);
            document.documentElement.style.setProperty('--ring', hsl);
            document.documentElement.style.setProperty('--tenant-primary', hex);
            // Inject accent overrides for non-shadcn elements
            let style = document.getElementById('tenant-accent-css');
            if (!style) { style = document.createElement('style'); style.id = 'tenant-accent-css'; document.head.appendChild(style); }
            style.textContent = `
              main .bg-blue-600 { background-color: ${hex} !important; }
              main .bg-blue-600:hover { filter: brightness(1.1); }
              main .text-blue-600 { color: ${hex} !important; }
              main .border-blue-600 { border-color: ${hex} !important; }
              main .bg-blue-500 { background-color: ${hex} !important; }
              main .text-blue-500 { color: ${hex} !important; }
              .dark main .bg-blue-600 { background-color: ${hex} !important; }
              .dark main .text-blue-600 { color: ${hex} !important; }
              .dark main .text-blue-400 { color: ${hex} !important; }
            `;
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    let activeKey = null;
    sidebarStructure.forEach(entry => {
      if (entry.type === 'group') {
        const hasActive = entry.items.some(item => item.path === location.pathname);
        if (hasActive) activeKey = entry.key;
      }
    });
    if (activeKey) {
      setOpenGroups(prev => {
        // Single-accordion: when navigating into a group, open ONLY that group
        // and collapse every other one. Mirrors the toggle behaviour below so
        // the sidebar feels consistent whether the user clicks the group header
        // or a link inside a group.
        if (prev[activeKey] && Object.entries(prev).every(([k, v]) => !v || k === activeKey)) {
          return prev;
        }
        const next = { [activeKey]: true };
        localStorage.setItem('sidebarGroups', JSON.stringify(next));
        return next;
      });
    }
  }, [location.pathname]);

  // Safety: on every route change, clear any stuck pointer-events lock that a previous
  // Radix/shadcn Dialog may have left behind on <body>. This prevents the whole page from
  // appearing "frozen" after navigating away from a dialog mid-close.
  useEffect(() => {
    const body = document.body;
    if (body) {
      body.style.pointerEvents = '';
      body.removeAttribute('data-scroll-locked');
    }
    // Remove orphaned Radix dialog overlays left in the DOM
    document.querySelectorAll('[data-state="open"][role="dialog"]').forEach(el => {
      if (!el.isConnected) return;
      if (!document.body.contains(el.parentElement)) el.remove();
    });
  }, [location.pathname]);


  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      // Single-accordion behaviour:
      //  - If the clicked group is closed → open it AND close every other.
      //  - If the clicked group is already open → just close it (no other open).
      const wasOpen = !!prev[key];
      const next = wasOpen ? {} : { [key]: true };
      localStorage.setItem('sidebarGroups', JSON.stringify(next));
      return next;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setPwdLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/change-password`, { old_password: oldPassword, new_password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setChangePwdOpen(false); setOldPassword(''); setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally { setPwdLoading(false); }
  };

  const effectiveRole = user && ['intern', 'it', 'finance', 'marketing'].includes(user.role) ? 'recruiter' : user?.role;

  const renderNavItem = (item, closeMobile) => {
    if (!user) return null;
    const role = effectiveRole;
    if (!item.roles.includes(role) && !(role === 'admin_support' && item.roles.includes('admin'))) return null;
    // Submodule check: path like "/positions" -> submodule "positions"
    const subId = item.path?.replace(/^\//, '');
    if (subId && disabledSubmodules.includes(subId)) return null;
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const displayLabel = item.tKey ? t(item.tKey, item.label) : item.label;
    return (
      <Link key={item.path} to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        onClick={closeMobile}
        className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
          isActive ? 'bg-blue-50 dark:bg-blue-500/15 text-blue-900 dark:text-blue-300 border-r-4 border-blue-600 font-bold' : 'text-slate-600 dark:text-slate-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-800'
        }`}>
        <Icon size={17} />
        {displayLabel}
      </Link>
    );
  };

  const SidebarContent = () => {
    const closeMobile = () => setIsMobileMenuOpen(false);
    return (
      <>
        <div className="p-5 border-b border-blue-100 dark:border-slate-700 bg-gradient-to-br from-blue-50 to-white dark:from-slate-800 dark:to-slate-900">
          <div className="flex items-center gap-3 mb-3">
            {tenantBranding.logo_url ? (
              <img src={tenantBranding.logo_url} alt="Logo" className="h-9 w-9 rounded-lg object-contain" />
            ) : (
              <div className="p-2 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
            )}
            <h1 className="text-xl font-extrabold tracking-tight text-blue-900 dark:text-white truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {tenantBranding.company_name || 'Recruitizr'}
            </h1>
          </div>
          {user && (
            <div className="p-2.5 bg-white dark:bg-slate-800 rounded border border-blue-100 dark:border-slate-600">
              <p className="text-sm font-semibold text-blue-900 dark:text-slate-100">{user.name}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400 uppercase tracking-wider mt-0.5">{user.role.replace('_', ' ')}</p>
            </div>
          )}
        </div>

        <nav ref={navScrollRef} onScroll={handleNavScroll} className="flex-1 py-2 overflow-y-auto">
          {sidebarStructure.map(entry => {
            if (enabledModules && entry.module && !enabledModules.includes(entry.module)) return null;
            // Feature flag check at module level
            if (entry.featureFlag && !isFeatureEnabled(entry.featureFlag)) return null;
            if (entry.type === 'item') {
              return renderNavItem(entry, closeMobile);
            }
            const visibleItems = entry.items.filter(item => {
              if (!user) return false;
              const role = effectiveRole;
              const roleMatch = item.roles.includes(role) || (role === 'admin_support' && item.roles.includes('admin'));
              if (!roleMatch) return false;
              // Feature flag check at item level
              if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            const isOpen = openGroups[entry.key];
            const hasActive = visibleItems.some(item => item.path === location.pathname);
            return (
              <div key={entry.key} className="mt-1">
                <button data-testid={`nav-group-${entry.key}`} onClick={() => toggleGroup(entry.key)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    hasActive ? 'text-blue-900 dark:text-blue-300 bg-blue-50/50 dark:bg-blue-500/10' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}>
                  <span className="flex items-center gap-2">
                    {entry.icon && <entry.icon size={14} />}
                    {entry.tKey ? t(entry.tKey, entry.label) : entry.label}
                  </span>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isOpen && (
                  <div className="ml-1 border-l-2 border-blue-100 dark:border-slate-700">
                    {visibleItems.map(item => renderNavItem(item, closeMobile))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200 dark:border-slate-700 space-y-0.5">
          <Link to="/profile">
            <Button data-testid="my-profile-button" variant="ghost"
              className="w-full justify-start gap-3 text-slate-600 dark:text-slate-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-800 rounded-none text-sm h-9">
              <UserCircle size={17} /> My Profile
            </Button>
          </Link>
          <Button onClick={() => { setChangePwdOpen(true); setOldPassword(''); setNewPassword(''); }}
            data-testid="change-password-button" variant="ghost"
            className="w-full justify-start gap-3 text-slate-600 dark:text-slate-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-800 rounded-none text-sm h-9">
            <KeyRound size={17} /> {t('nav.changePassword', 'Change Password')}
          </Button>
          <Button onClick={handleLogout} data-testid="logout-button" variant="ghost"
            className="w-full justify-start gap-3 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-none text-sm h-9">
            <LogOut size={17} /> {t('nav.logout', 'Logout')}
          </Button>
        </div>
      </>
    );
  };

  const isImpersonating = !!localStorage.getItem('impersonation');

  return (
    <div className={`flex min-h-screen bg-white dark:bg-[#0c1222] ${isImpersonating ? 'pt-10' : ''}`}>
      <aside className={`hidden lg:flex lg:flex-col w-60 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0f1629] h-screen fixed left-0 z-40 ${isImpersonating ? 'top-10' : 'top-0'}`}>
        <SidebarContent />
      </aside>

      <div className="flex-1 min-w-0 lg:ml-60 overflow-x-hidden">
        <header className="lg:hidden sticky top-0 z-30 bg-white dark:bg-[#0f1629] border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {tenantBranding.logo_url ? (
              <img src={tenantBranding.logo_url} alt="Logo" className="h-7 w-7 rounded object-contain" />
            ) : null}
            <h1 className="text-xl font-extrabold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {tenantBranding.company_name || 'Recruitizr'}
            </h1>
          </div>
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" className="dark:text-white"><Menu size={24} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-60 p-0 dark:bg-[#0f1629] dark:border-slate-700">
              <div className="flex flex-col h-full"><SidebarContent /></div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="p-4 sm:p-6 md:p-8 lg:p-12 grid-bg min-h-screen dark:text-slate-200">
          <ExitSurveyBanner />
          <Outlet />
        </main>
      </div>

      <Dialog open={changePwdOpen} onOpenChange={setChangePwdOpen}>
        <DialogContent className="rounded dark:bg-[#0f1629] dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Password</DialogTitle>
            <DialogDescription className="dark:text-slate-400">Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-slate-300">Current Password</Label>
              <Input data-testid="old-password-input" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} required className="mt-2 rounded border-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white" placeholder="Enter current password" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-slate-300">New Password</Label>
              <Input data-testid="new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="mt-2 rounded border-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white" placeholder="Minimum 6 characters" />
            </div>
            <Button data-testid="submit-change-password" type="submit" disabled={pwdLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              {pwdLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <ChatWidget />
    </div>
  );
}
